import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { PullDataService } from '../../service/PullDataService';
import { useAuthStore } from '../../store/storeAuth';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import Label from '../UI/Label/Label';
import SearchableSelect from '../SearchableSelect';
import TagInput from '../Test/TagInput';

const ROOT_OPTION = { label: 'Root (no folder)', value: '' };

const LoadingPlaceholder = ({
  theme,
  label,
}: {
  theme?: string;
  label: string;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: '8px' }}>
    <CircularProgress
      size={16}
      sx={{ color: theme === 'dark' ? '#D0D5DD' : '#667085' }}
    />
    <Typography
      sx={{ fontSize: '13px', color: theme === 'dark' ? '#98A2B3' : '#667085' }}
    >
      {label}
    </Typography>
  </Box>
);

interface PushMicrosoftRawFilesDataProps {
  formData: any;
  onChange: (e: any) => void;
  onChangeSelect: (e: { target: { name: string; value: any } }) => void;
  data: any;
  nodeId: string;
  nodeLabel: string;
}

const PushMicrosoftRawFilesData: React.FC<PushMicrosoftRawFilesDataProps> = ({
  formData,
  onChange,
  onChangeSelect,
  data,
  nodeId,
}) => {
  const { user } = useAuthStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);

  const [folderOptions, setFolderOptions] = useState<{ label: string; value: string }[]>([ROOT_OPTION]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const selectedFolderId = data?.microsoftRawFilesFolderId ?? '';

  const loadFolders = useCallback(async () => {
    setLoadingFolders(true);
    try {
      const response = await PullDataService.getMicrosoftFolders();
      const folders = response.data?.folders ?? [];
      setFolderOptions([
        ROOT_OPTION,
        ...folders.map((f: { id: string; name: string }) => ({
          label: f.name,
          value: f.id,
        })),
      ]);
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
      setFolderOptions([ROOT_OPTION]);
    } finally {
      setLoadingFolders(false);
      setInitialLoadDone(true);
    }
  }, [setNotification]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  const searchableSelectSx = {
    '& .MuiList-root': {
      backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    },
    '& .MuiSvgIcon-root': {
      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
    },
    borderRadius: '8px',
    border: 'none',
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
  };

  const menuPropsSx = {
    PaperProps: {
      sx: {
        '& .Mui-selected': {
          backgroundColor: user?.theme === 'dark' ? '#667085' : '#D0D5DD',
        },
        backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
      },
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '360px' }}>
      <Box>
        <Label
          labelName="Folder"
          isAdvanced
          labelStyle={{
            fontSize: '14px',
            color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            fontWeight: 400,
          }}
        />
        {loadingFolders && !initialLoadDone ? (
          <LoadingPlaceholder theme={user?.theme} label="Loading folders..." />
        ) : (
          <SearchableSelect
            options={folderOptions}
            label="Select folder or leave as Root"
            loading={loadingFolders}
            loadingText="Searching..."
            filterOptions={x => x}
            fullWidth
            onChange={value => {
              const folderId = value ? value.value : '';
              onChangeSelect({
                target: { name: 'microsoftRawFilesFolderId', value: folderId },
              });
            }}
            value={
              folderOptions.find(o => o.value === selectedFolderId) ||
              ROOT_OPTION
            }
            className="nodrag"
            sx={searchableSelectSx}
            MenuProps={menuPropsSx}
          />
        )}
      </Box>

      <Box sx={{ margin: '10px 0' }}>
        <TagInput
          key={nodeId}
          placeholder="Enter file name"
          onChange={onChange}
          value={formData.fileName}
          dataConnected={data?.fileNameData || []}
          isAdvanced
          labelName="New File Name"
          isCollapsed={false}
          handleId="fileName"
          nodeId={nodeId}
          advancedCollapsed
          name="fileName"
          fullWidth
          InputProps={{
            style: {
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            },
          }}
          labelStyle={{ marginTop: '16px' }}
          sx={{
            color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
            border: `2px solid ${
              user?.theme === 'dark' ? '#475467' : '#EAECF0'
            }`,
          }}
          handleSettings={{ top: '50.5px' }}
        />
      </Box>
    </Box>
  );
};

export default PushMicrosoftRawFilesData;
